/**
 * useMapView Hook
 *
 * Manages map view state and movement logic
 * following Single Responsibility Principle
 */

import {useState, useCallback, useRef, useEffect} from 'react';
import {MapConfigurationService} from '../../Services/map/MapConfigurationService';

export interface UseMapViewProps {
  center: { lat: number; lng: number };
  onBoundsUpdate: (bounds: any) => void;
  onCenterUpdate: (lat: number, lng: number) => void;
}

export interface UseMapViewReturn {
  viewState: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  isUpdatingData: boolean;
  handleMoveStart: () => void;
  handleMove: (e: any) => void;
  handleMoveEnd: (e: any) => void;
}

export const useMapView = ({
  center,
  onBoundsUpdate,
  onCenterUpdate
}: UseMapViewProps): UseMapViewReturn => {
  const mapConfig = MapConfigurationService.getInstance();
  const defaultCenter = mapConfig.getDefaultCenter();
  const defaultZoom = mapConfig.getDefaultZoom();

  // Validate center coordinates
  const isValidCenter = mapConfig.validateCoordinates(center.lat, center.lng);

  const [viewState, setViewState] = useState({
    latitude: isValidCenter ? center.lat : defaultCenter.lat,
    longitude: isValidCenter ? center.lng : defaultCenter.lng,
    zoom: defaultZoom
  });

  const [isUpdatingData, setIsUpdatingData] = useState(false);
  const moveEndTimeoutRef = useRef<number>();

  // Update view state when center changes
  useEffect(() => {
    setViewState((prev) => {
      const newLat = isValidCenter ? center.lat : defaultCenter.lat;
      const newLng = isValidCenter ? center.lng : defaultCenter.lng;

      if (Math.abs(prev.latitude - newLat) < 0.0001 && Math.abs(prev.longitude - newLng) < 0.0001) {
        return prev;
      }
      return {
        ...prev,
        latitude: newLat,
        longitude: newLng
      };
    });
  }, [center.lat, center.lng, isValidCenter, defaultCenter.lat, defaultCenter.lng]);

  const handleMoveStart = useCallback(() => {
    setIsUpdatingData(true);
    if (moveEndTimeoutRef.current) {
      clearTimeout(moveEndTimeoutRef.current);
    }
  }, []);

  const handleMove = useCallback((e: any) => {
    setViewState(e.viewState);
  }, []);

  const handleMoveEnd = useCallback((e: any) => {
    if (moveEndTimeoutRef.current) {
      clearTimeout(moveEndTimeoutRef.current);
    }

    moveEndTimeoutRef.current = setTimeout(() => {
      const bounds = e.target.getBounds();
      onBoundsUpdate({
        sw: {
          lat: bounds.getSouthWest().lat,
          lng: bounds.getSouthWest().lng
        },
        ne: {
          lat: bounds.getNorthEast().lat,
          lng: bounds.getNorthEast().lng
        }
      });
      onCenterUpdate(e.viewState.latitude, e.viewState.longitude);

      setTimeout(() => {
        setIsUpdatingData(false);
      }, 300);
    }, 500);
  }, [onBoundsUpdate, onCenterUpdate]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (moveEndTimeoutRef.current) {
        clearTimeout(moveEndTimeoutRef.current);
      }
    };
  }, []);

  return {
    viewState,
    isUpdatingData,
    handleMoveStart,
    handleMove,
    handleMoveEnd
  };
};
